import React, { useId, useRef, useState } from 'react';
import { AlertCircle, FileText, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@/utils/files';
import Icon from './Icon';

interface FileDropzoneProps {
  label: string;
  file: File | null;
  /** Called with the chosen or dropped file; validation is the caller's job. */
  onFile: (file: File) => void;
  onRemove: () => void;
  /** e.g. "application/pdf,image/jpeg,image/png". */
  accept: string;
  /** e.g. "PDF, JPG or PNG, up to 5 MB". */
  hint: string;
  error?: string;
}

/** Click or drag-and-drop file field (Figma "Upload invoice"); shows the chosen file once picked. */
const FileDropzone: React.FC<FileDropzoneProps> = ({ label, file, onFile, onRemove, accept, hint, error }) => {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <label htmlFor={id} className="tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-ds-text-secondary">
        {label}
      </label>
      <input
        ref={input}
        id={id}
        type="file"
        accept={accept}
        className="tw-sr-only"
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) onFile(picked);
          e.target.value = '';
        }}
      />
      {file && !error ? (
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-4">
          <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-brand-tint tw-text-ds-brand-text">
            <Icon icon={FileText} size={20} />
          </span>
          <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
            <span className="tw-truncate tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{file.name}</span>
            <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{formatFileSize(file.size)}</span>
          </span>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${file.name}`}
            className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-[10px] tw-text-ds-text-secondary hover:tw-bg-ds-subtle focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
          >
            <Icon icon={X} size={18} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) onFile(dropped);
          }}
          className={cn(
            'tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-xl tw-border-[1.5px] tw-border-dashed tw-px-4 tw-py-6 tw-text-center tw-transition-colors',
            'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand',
            error
              ? 'tw-border-ds-danger-fg tw-bg-ds-danger-bg'
              : dragging
                ? 'tw-border-ds-brand tw-bg-ds-brand-tint'
                : 'tw-border-ds-border-strong tw-bg-ds-surface hover:tw-bg-ds-subtle',
          )}
        >
          <Icon icon={Upload} size={20} className={error ? 'tw-text-ds-danger-fg' : 'tw-text-ds-brand-text'} />
          <span className="tw-text-[15px] tw-leading-5 tw-text-ds-text-secondary">
            <span className="tw-font-semibold tw-text-ds-text-primary">Choose a file</span> or drag it here
          </span>
          <span className="tw-text-[12px] tw-leading-4 tw-text-ds-text-tertiary">{hint}</span>
        </button>
      )}
      {error && (
        <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-[12px] tw-font-medium tw-leading-4 tw-text-ds-danger-fg">
          <Icon icon={AlertCircle} size={14} className="tw-mt-px" />
          {error}
        </p>
      )}
    </div>
  );
};

export default FileDropzone;
